import { Checkbox, Select, Spin } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AppModal } from '@components/form/Modal';
import { AppInput } from '@components/form/input/app-input';
import { AppAlert } from '@components/antd';
import { Label } from '@components/form/text-area/app-text-area';
import { AppSelect } from '@components/form/select/app-select';
import { capitalize } from '@shared/utils/capitalize';
import { AppSelectCountry } from '@modules/settings/my-numbers/purchase-number/components/app-select-country';
import {
  filterCountriesByAlphaTwoCode,
  matchToOptions,
} from '@modules/settings/my-numbers/purchase-number/constants/purchase-number.constant';
import { ClaimNumbersModalProps } from '../claim-numbers.types';
import { useClaimNumbersForm } from '../hooks/use-claim-numbers-form';
import { ClaimNumberTypeSelect } from './claim-number-type-select';
import { BundleVerificationModal } from './bundle-verification-modal';
import React, { useRef } from 'react';

const selectOptions = [
  { label: 'Voice', value: 'voice' },
  { label: 'SMS', value: 'SMS' },
  { label: 'MMS', value: 'MMS' },
];

export function ClaimNumbersModal(props: ClaimNumbersModalProps) {
  const { t } = useTranslation();
  const {
    availableRewardCount,
    numberClaimForm,
    numberSearchResult,
    fields,
    selectedIds,
    numbersError,
    claimLoading,
    handleSearch,
    handleMatch,
    handleNumbersChange,
    handleSubmit,
    onClaimLater,
    bundleVerificationModal,
    pendingBundleSelection,
    pendingCapabilities,
    bundleFieldRequirements,
    onBundleRequested,
  } = useClaimNumbersForm(props);
  const numberSelectRef = useRef<React.ComponentRef<typeof Select>>(null);

  console.log('TESt 123333');

  return (
    <AppModal
      open
      afterOpenChange={(isOpen) => isOpen && numberSelectRef?.current?.focus()}
      title={
        <AppModal.Header>
          <AppModal.Title>{t('get_your_free_phone_number')}</AppModal.Title>
          <AppModal.SubTitle>
            {t('claim_your_free_phone_numbers_with_your_subscription', { count: availableRewardCount })}
          </AppModal.SubTitle>
        </AppModal.Header>
      }
      classNames={{ content: '!max-w-[420px]' }}
      footer={
        <AppModal.Footer>
          <AppModal.Button
            className="w-full"
            onClick={onClaimLater}
            disabled={claimLoading}
            data-test-id="claim-number-later-button"
          >
            {capitalize(t('claim_later'))}
          </AppModal.Button>
          <AppModal.Button
            className="w-full"
            color="primary"
            onClick={numberClaimForm?.handleSubmit(handleSubmit)}
            loading={claimLoading}
            data-test-id="claim-number-submit-button"
          >
            {capitalize(t('claim_number'))}
          </AppModal.Button>
        </AppModal.Footer>
      }
      onCancel={onClaimLater}
    >
      <form className="space-y-3.5 max-h-150 overflow-y-auto" data-test-id="claim-number-form">
        <AppSelectCountry
          source={filterCountriesByAlphaTwoCode(['US', 'CA', 'GB'])}
          name="isoCountry"
          control={numberClaimForm?.control}
          label={capitalize(t('country_one'))}
          showSearch={false}
          required
          data-test-id="claim-number-country-select"
        />

        {numberClaimForm?.watch('isoCountry') === 'US' && (
          <AppAlert
            showIcon
            type="warning"
            className="app-alert mb-3.5!"
            message="Please be advised that certain third-party platforms may have limitations when accepting these numbers for verification, depending on their internal policies."
          />
        )}

        <div className="flex flex-col gap-1.5 w-full">
          <Label required>{capitalize(t('select_capabilities'))}</Label>

          <Controller
            name="capabilities"
            control={numberClaimForm?.control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className="w-full flex flex-col gap-1.5" data-test-id="claim-number-capabilities-checkbox-group">
                <Checkbox.Group value={value} options={selectOptions} onChange={onChange} />

                {error && <p className="text-sm font-normal text-red-500">{error.message}</p>}
              </div>
            )}
          />
        </div>

        {numberClaimForm?.watch('isoCountry') === 'GB' && <ClaimNumberTypeSelect control={numberClaimForm?.control} />}

        <AppSelect
          className="[&_.ant-select-selection-item]:text-base [&_.ant-select-selection-item]:text-text-400 [&_.ant-select-selection-item]:font-medium!"
          label={capitalize(t('match_to'))}
          control={numberClaimForm?.control}
          name="matchTo"
          options={matchToOptions}
          onChange={handleMatch}
          data-test-id="claim-number-match-to-select"
        />

        <div className="flex flex-col gap-1.5" data-test-id="claim-number-search-select">
          <Label required>{capitalize(t('phone_number'))}</Label>

          <Select
            ref={numberSelectRef}
            mode="multiple"
            maxCount={availableRewardCount}
            value={selectedIds}
            placeholder={t('search_number')}
            onChange={handleNumbersChange}
            onSearch={handleSearch}
            filterOption={false}
            className="[&_.ant-select-selection-placeholder]:text-base"
            options={
              numberSearchResult?.phoneNumbers?.length > 0
                ? numberSearchResult.phoneNumbers.map((num) => ({ label: num.id, value: num.id }))
                : []
            }
            notFoundContent={
              numberSearchResult?.loading ? (
                <div className="flex items-center justify-center w-full h-25">
                  <Spin size="large" />
                </div>
              ) : (
                <div className="flex items-center justify-center w-full h-25 capitalize!">{t('no_data_available')}</div>
              )
            }
          />

          <p className="text-sm text-text-500">
            {t('numbers_selected_of_total', { selected: selectedIds.length, total: availableRewardCount })}
          </p>

          {numbersError && <p className="text-red-500 text-sm">{numbersError}</p>}
        </div>

        {fields.length > 0 && (
          <div className="flex flex-col gap-2.5">
            <Label required>{capitalize(t('friendly_name'))}</Label>
            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-col gap-1">
                <p className="text-sm font-medium text-text-700">{field.phoneNumber}</p>
                <AppInput
                  control={numberClaimForm?.control}
                  name={`numbers.${index}.friendlyName`}
                  placeholder={t('enter_friendly_name')}
                  className="[&_.ant-input::placeholder]:font-medium"
                  data-test-id={`claim-number-friendly-name-input-${index}`}
                />
              </div>
            ))}
          </div>
        )}
      </form>

      {pendingBundleSelection && (
        <BundleVerificationModal
          open={bundleVerificationModal.open}
          onClose={bundleVerificationModal.handleClose}
          selections={pendingBundleSelection}
          capabilities={pendingCapabilities}
          fieldRequirements={bundleFieldRequirements}
          onRequested={onBundleRequested}
        />
      )}
    </AppModal>
  );
}
